import { useMemo } from "react";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount, formatMs, formatMsTick } from "../../format";
import { NEUTRAL, SERIES, SERIES_LIGHT, STATUS } from "../../palette";
import type { MetricSeries, PoolSnapshot } from "../../types";
import { LIMITS } from "../backend/tone";
import { counterSeries, hasHist, histField, podBeyondPalette, podColor } from "./pod-order";

/** The window charts of the pod pool (metrics of the board's window and scope). */
export function PodWindowCharts({
  metrics,
  order,
  pool,
}: {
  metrics: MetricSeries;
  order: string[];
  pool: PoolSnapshot | null;
}) {
  const { t } = useLocalization();
  const windowLabel = t(`monitoring.window.long.${metrics.window}` as TranslationKey);
  const maxConcurrency = pool?.max_concurrency ?? null;

  const charts = useMemo(() => {
    const perPod = (prefix: string): ChartSeries[] =>
      order
        .map((endpoint, index) => ({ endpoint, index }))
        .filter(({ endpoint }) => hasHist(metrics, `${prefix}@${endpoint}`))
        .map(({ endpoint, index }) => ({
          key: `${prefix}@${endpoint}`,
          label: endpoint,
          color: podColor(index),
          values: histField(metrics, `${prefix}@${endpoint}`, "p95"),
          type: "line" as const,
          dashed: podBeyondPalette(index),
          hidden: podBeyondPalette(index),
        }));

    const healthy = metrics.mins["pods.healthy"] ?? [];
    const total = metrics.maxes["pods.total"] ?? [];

    return {
      // an empty pool samples as zeros: that is data (critical), not "no data"
      healthSampled: total.some((v) => typeof v === "number") || healthy.some((v) => typeof v === "number"),
      requests: [
        { key: "ok", label: t("monitoring.pods.series.ok"), color: NEUTRAL.mid, values: counterSeries(metrics, "pod.ok"), type: "bar", stackId: "o" },
        { key: "fail", label: t("monitoring.pods.series.fail"), color: STATUS.critical, values: counterSeries(metrics, "pod.fail"), type: "bar", stackId: "o" },
        { key: "timeout", label: t("monitoring.pods.series.timeout"), color: STATUS.serious, values: counterSeries(metrics, "pod.timeout"), type: "bar", stackId: "o" },
      ] satisfies ChartSeries[],
      latency: [
        { key: "p50", label: t("monitoring.series.p50"), color: SERIES_LIGHT, values: histField(metrics, "pod.request_ms", "p50"), type: "line" },
        { key: "p95", label: t("monitoring.series.p95"), color: SERIES[0], values: histField(metrics, "pod.request_ms", "p95"), type: "line" },
        { key: "crust", label: t("monitoring.pods.series.crustP95"), color: SERIES[1], values: histField(metrics, "pod.crust_ms", "p95"), type: "line", dashed: true, hidden: true },
        { key: "components", label: t("monitoring.pods.series.componentsP95"), color: SERIES[2], values: histField(metrics, "pod.components_ms", "p95"), type: "line", dashed: true, hidden: true },
      ] satisfies ChartSeries[],
      perPod: perPod("pod.request_ms"),
      ping: perPod("pod.ping_ms"),
      queueWait: [
        { key: "p95", label: t("monitoring.series.p95"), color: SERIES[0], values: histField(metrics, "pod.queue_ms", "p95"), type: "line" },
        { key: "max", label: t("monitoring.pods.series.max"), color: NEUTRAL.baseline, values: histField(metrics, "pod.queue_ms", "max"), type: "line", dashed: true },
      ] satisfies ChartSeries[],
      concurrency: [
        { key: "in_flight", label: t("monitoring.pods.series.inFlight"), color: NEUTRAL.strong, values: metrics.maxes["pod.in_flight"] ?? [], type: "line", step: true },
        { key: "queued", label: t("monitoring.pods.series.queued"), color: STATUS.warning, values: metrics.maxes["pod.queued"] ?? [], type: "bar" },
      ] satisfies ChartSeries[],
      health: [
        {
          key: "unhealthy",
          label: t("monitoring.pods.series.unhealthy"),
          color: STATUS.critical,
          values: metrics.t.map((_, i) => {
            const all = total[i];
            const ok = healthy[i];
            return typeof all === "number" && typeof ok === "number" ? Math.max(0, all - ok) : null;
          }),
          type: "bar",
        },
        { key: "healthy", label: t("monitoring.pods.series.healthy"), color: NEUTRAL.strong, values: healthy, type: "line", step: true },
        { key: "total", label: t("monitoring.pods.series.total"), color: NEUTRAL.baseline, values: total, type: "line", step: true, dashed: true },
      ] satisfies ChartSeries[],
      retries: [
        { key: "retried", label: t("monitoring.pods.series.retriedOk"), color: STATUS.warning, values: counterSeries(metrics, "pod.retried_ok"), type: "bar", stackId: "r" },
        { key: "pool_error", label: t("monitoring.pods.series.poolError"), color: STATUS.critical, values: counterSeries(metrics, "pod.pool_error"), type: "bar", stackId: "r" },
      ] satisfies ChartSeries[],
    };
  }, [metrics, order, t]);

  const common = { t: metrics.t, stepMs: metrics.step_ms, window: metrics.window };
  const extraPods = order.length > SERIES.length ? ` ${t("monitoring.pods.chart.extraPods")}` : "";

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <SectionCard title={t("monitoring.pods.chart.requests")} description={t("monitoring.pods.chart.requestsDescription")}>
        <TimeSeriesChart {...common} series={charts.requests} integer format={formatCount} emptyText={t("monitoring.pods.chart.empty")} />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.latency")} description={t("monitoring.pods.chart.latencyDescription")}>
        <TimeSeriesChart
          {...common}
          series={charts.latency}
          format={formatMs}
          tickFormat={formatMsTick}
          referenceLines={[{ y: LIMITS.podSlowMs, label: t("monitoring.pods.chart.slow"), color: STATUS.warning }]}
          emptyText={t("monitoring.pods.chart.empty")}
        />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.perPod")} description={`${t("monitoring.pods.chart.perPodDescription")}${extraPods}`}>
        <TimeSeriesChart
          key={charts.perPod.map((s) => s.key).join("|")}
          {...common}
          series={charts.perPod}
          format={formatMs}
          tickFormat={formatMsTick}
          emptyText={t("monitoring.pods.chart.empty")}
        />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.ping")} description={`${t("monitoring.pods.chart.pingDescription")}${extraPods}`}>
        <TimeSeriesChart
          key={charts.ping.map((s) => s.key).join("|")}
          {...common}
          series={charts.ping}
          format={formatMs}
          tickFormat={formatMsTick}
          emptyText={t("monitoring.pods.chart.noPing")}
        />
      </SectionCard>
      <SectionCard
        title={t("monitoring.pods.chart.queue")}
        description={t("monitoring.pods.chart.queueDescription", { max: maxConcurrency ?? "–" })}
      >
        <TimeSeriesChart
          {...common}
          series={charts.queueWait}
          format={formatMs}
          tickFormat={formatMsTick}
          emptyText={t("monitoring.pods.chart.noQueue")}
        />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.concurrency")} description={t("monitoring.pods.chart.concurrencyDescription")}>
        <TimeSeriesChart
          {...common}
          series={charts.concurrency}
          integer
          format={formatCount}
          referenceLines={
            maxConcurrency
              ? [{ y: maxConcurrency, label: t("monitoring.pods.chart.slots", { max: maxConcurrency }), color: STATUS.warning }]
              : undefined
          }
          emptyText={t("monitoring.pods.chart.noConcurrency")}
        />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.health")} description={t("monitoring.pods.chart.healthDescription")}>
        <TimeSeriesChart
          {...common}
          series={charts.health}
          integer
          format={formatCount}
          emptyText={charts.healthSampled ? undefined : t("monitoring.pods.chart.noHealth")}
        />
      </SectionCard>
      <SectionCard title={t("monitoring.pods.chart.retries")} description={t("monitoring.pods.chart.retriesDescription")}>
        <TimeSeriesChart
          {...common}
          series={charts.retries}
          integer
          format={formatCount}
          emptyText={t("monitoring.pods.chart.noRetries", { window: windowLabel })}
        />
      </SectionCard>
    </div>
  );
}
